"use client";

import { c as _c } from "react/compiler-runtime";
import { IconPlusOutline18, IconTrashOutline18 } from "nucleo-ui-outline-18";
import { TokenIcon } from "@/components/ui/token-icon";
import { evaluateEmailMatch } from "@/lib/workflow/emailMatch";
import { useCallback, useEffect, useMemo, useState } from "react";
import JsonEditorBase from "../../../_components/form-fields/JsonEditorBase";
import { AUTO_GENERATED_TRIGGER_FIELDS, generateSampleFromSchema } from "../sampleGenerators";
import { getArchetypeConfig } from "./registry";
import type { TriggerArchetypeProps } from "./types";
function parseStringArrayValue(raw: string): string[] {
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map(value => String(value ?? ""));
    }
  } catch {
    // Fall through to line-based parsing.
  }
  const trimmed = raw.trim();
  if (!trimmed) return [""];
  return trimmed.split("\n").map(value => value.trim()).filter(Boolean);
}
function EmailRecipientsInput(t0) {
  const $ = _c(27);
  const {
    label,
    description,
    value,
    onChange
  } = t0;
  let addRecipient;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== description || $[1] !== label || $[2] !== onChange || $[3] !== value) {
    const recipients = parseStringArrayValue(value);
    const rows = recipients.length > 0 ? recipients : [""];
    const updateRecipient = (index, nextValue) => {
      const nextRecipients = rows.map((recipient, recipientIndex) => recipientIndex === index ? nextValue : recipient);
      onChange(JSON.stringify(nextRecipients.filter(_temp), null, 2));
    };
    addRecipient = () => {
      onChange(JSON.stringify([...rows, ""], null, 2));
    };
    const removeRecipient = index_0 => {
      const nextRecipients_0 = rows.filter((_, recipientIndex_0) => recipientIndex_0 !== index_0);
      onChange(JSON.stringify(nextRecipients_0.length > 0 ? nextRecipients_0 : [""], null, 2));
    };
    t3 = "space-y-2";
    let t5;
    if ($[9] !== label) {
      t5 = <span className="field-label">{label}</span>;
      $[9] = label;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] !== description) {
      t6 = description && <p className="body-sm text-ds-text-muted">{description}</p>;
      $[11] = description;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    if ($[13] !== t5 || $[14] !== t6) {
      t4 = <div className="space-y-0.5">{t5}{t6}</div>;
      $[13] = t5;
      $[14] = t6;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    t1 = "space-y-2";
    t2 = rows.map((recipient_1, index_1) => <div key={`${index_1}-${recipient_1}`} className="flex items-center gap-2"><input type="email" value={recipient_1} onChange={e => updateRecipient(index_1, e.target.value)} placeholder={index_1 === 0 ? "user@example.com" : "another@example.com"} className="border-ds-neutral-300 text-ds-text-primary placeholder:text-ds-text-disabled dark:border-ds-neutral-700 bg-ds-surface-card w-full rounded-lg border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" /><button type="button" onClick={() => removeRecipient(index_1)} disabled={rows.length === 1} className="border-ds-border-default text-ds-text-disabled hover:border-ds-neutral-300 hover:text-ds-text-subtle flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-40 dark:hover:border-zinc-600 dark:hover:text-zinc-300" aria-label={`Remove recipient ${index_1 + 1}`}><IconTrashOutline18 className="h-4 w-4" /></button></div>);
    $[0] = description;
    $[1] = label;
    $[2] = onChange;
    $[3] = value;
    $[4] = addRecipient;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    addRecipient = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <IconPlusOutline18 className="h-3.5 w-3.5" />;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== addRecipient) {
    t6 = <button type="button" onClick={addRecipient} className="label label-xs text-ds-text-muted hover:text-ds-text-secondary inline-flex items-center gap-1.5 transition-colors dark:hover:text-zinc-200">{t5}Add recipient</button>;
    $[17] = addRecipient;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t1 || $[20] !== t2 || $[21] !== t6) {
    t7 = <div className={t1}>{t2}{t6}</div>;
    $[19] = t1;
    $[20] = t2;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t3 || $[24] !== t4 || $[25] !== t7) {
    t8 = <div className={t3}>{t4}{t7}</div>;
    $[23] = t3;
    $[24] = t4;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
}

/**
 * FormInput archetype — editable form fields generated from the trigger's resultSchema.
 *
 * Used for triggers where the user might want to customize the test payload:
 * webhook, token-price-cexes, farcaster-*, polymarket-*, email-*, stripe-*.
 */
function _temp(recipient_0) {
  return recipient_0.trim().length > 0;
}
export function FormInput({
  triggerType,
  triggerPayload,
  resultSchema,
  onRun
}: TriggerArchetypeProps) {
  const config = getArchetypeConfig(triggerType);
  const isEmailTrigger = triggerType === "email-new-email-received" || triggerType === "email-new-email-matching-criteria";
  const isEmailMatchingTrigger = triggerType === "email-new-email-matching-criteria";
  const selectedAsset = triggerType === "token-price-cexes" ? triggerPayload?.asset as string | undefined : undefined;
  const selectedAssetLabel = selectedAsset || "Asset";
  const tokenPriceOperation = triggerType === "token-price-cexes" ? triggerPayload?.operation as string | undefined : undefined;
  const tokenPriceTarget = triggerType === "token-price-cexes" && triggerPayload?.targetPrice != null ? Number(triggerPayload.targetPrice) : undefined;
  const sampleData = useMemo(() => generateSampleFromSchema(resultSchema, triggerType, triggerPayload, config.sampleOverrides), [resultSchema, triggerType, triggerPayload, config.sampleOverrides]);
  const [formValues, setFormValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const [key, value] of Object.entries(sampleData)) {
      initial[key] = typeof value === "object" ? JSON.stringify(value, null, 2) : String(value ?? "");
    }
    return initial;
  });
  const emailMatch = useMemo(() => {
    if (!isEmailMatchingTrigger) return null;
    return evaluateEmailMatch({
      from: formValues.from ?? "",
      subject: formValues.subject ?? ""
    }, {
      fromFilter: triggerPayload?.fromFilter as string | undefined,
      subjectFilter: triggerPayload?.subjectFilter as string | undefined
    });
  }, [isEmailMatchingTrigger, formValues.from, formValues.subject, triggerPayload?.fromFilter, triggerPayload?.subjectFilter]);
  const editableFields = useMemo(() => {
    if (!resultSchema?.properties) return [];
    return Object.entries(resultSchema.properties).filter(([key, prop]) => !AUTO_GENERATED_TRIGGER_FIELDS.includes(key) && !prop.readOnly).filter(([key, prop]) => !config.sampleOverrides?.skipKey?.(key, prop, triggerPayload)).map(([key, prop]) => ({
      key,
      type: prop.type ?? "string",
      description: prop.description
    }));
  }, [resultSchema, config.sampleOverrides, triggerPayload]);

  // Fields that are auto-computed (skipped from editing) — compute their values
  // directly via getValueOverride since skipKey excludes them from sampleData
  const autoFields = useMemo(() => {
    if (!resultSchema?.properties || !config.sampleOverrides?.skipKey) return [];
    return Object.entries(resultSchema.properties).filter(([key, prop]) => !AUTO_GENERATED_TRIGGER_FIELDS.includes(key) && !prop.readOnly && config.sampleOverrides?.skipKey?.(key, prop, triggerPayload)).map(([key, prop]) => ({
      key,
      value: config.sampleOverrides?.getValueOverride?.(key, prop, triggerPayload) ?? sampleData[key]
    })).filter(f => f.value != null);
  }, [resultSchema, config.sampleOverrides, triggerPayload, sampleData]);

  // Full payload = sampleData + auto-computed fields that skipKey excluded
  const fullPayload = useMemo(() => {
    const payload = {
      ...sampleData
    };
    for (const f of autoFields) {
      payload[f.key] = f.value;
    }
    return payload;
  }, [sampleData, autoFields]);
  const tokenPricePreview = useMemo(() => {
    if (triggerType !== "token-price-cexes" || !selectedAsset) return null;
    const priceField = autoFields.find(field => field.key === "price");
    const samplePrice = typeof priceField?.value === "number" ? priceField.value : priceField?.value != null ? Number(priceField.value) : undefined;
    const operationLabel = {
      ">": "rises above",
      ">=": "rises to or above",
      "<": "falls below",
      "<=": "falls to or below",
      "==": "equals"
    }[tokenPriceOperation || ""] || "reaches";
    return {
      samplePrice,
      operationLabel
    };
  }, [autoFields, selectedAsset, tokenPriceOperation, triggerType]);
  const handleFieldChange = useCallback((key: string, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);
  const handleRun = useCallback(() => {
    const payload: Record<string, unknown> = {
      ...fullPayload
    };
    for (const field of editableFields) {
      const raw = formValues[field.key] ?? "";
      switch (field.type) {
        case "number":
        case "integer":
          {
            const n = Number(raw);
            payload[field.key] = isNaN(n) ? 0 : field.type === "integer" ? Math.floor(n) : n;
            break;
          }
        case "boolean":
          payload[field.key] = raw === "true";
          break;
        case "object":
        case "array":
          try {
            payload[field.key] = JSON.parse(raw);
          } catch {
            payload[field.key] = field.type === "array" ? [] : {};
          }
          break;
        default:
          payload[field.key] = raw;
      }
    }
    onRun(payload);
  }, [editableFields, formValues, onRun, fullPayload]);

  // Notify parent of current payload so the shell's Run button can use it
  useEffect(() => {
    handleRun();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onRun is a stable state setter
  }, [formValues, fullPayload]);
  if (editableFields.length === 0 && autoFields.length === 0) {
    return <p className="body-sm text-ds-text-muted">Test data will be generated automatically.</p>;
  }
  return <div className="space-y-3">
      {emailMatch && <div className="space-y-1.5">
          {!emailMatch.hasConfiguredFilter ? <div className="border-ds-border-default dark:bg-ds-neutral-900/40 rounded-lg border bg-zinc-50 px-4 py-3">
              <p className="body-sm text-ds-text-subtle">
                No filter configured — any email to your inbox will trigger this workflow.
              </p>
            </div> : emailMatch.matches ? <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-900/60 dark:bg-emerald-950/30">
              <p className="body-sm-semibold text-emerald-700 dark:text-emerald-300">
                Matches your filter — this sample would trigger the workflow.
              </p>
              <ul className="mt-1 space-y-0.5">
                {emailMatch.fields.map(f => <li key={f.field} className="caption-xs text-emerald-700/80 dark:text-emerald-300/80">
                    <span className="font-medium">{f.field === "from" ? "fromFilter" : "subjectFilter"}</span> &ldquo;
                    {f.filter}&rdquo; found in {f.field} &ldquo;{f.value}&rdquo;
                  </li>)}
              </ul>
            </div> : <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/60 dark:bg-amber-950/30">
              <p className="body-sm-semibold text-amber-800 dark:text-amber-200">
                Wouldn&rsquo;t trigger in production — this sample doesn&rsquo;t match your filter.
              </p>
              <ul className="mt-1 space-y-0.5">
                {emailMatch.fields.map(f => <li key={f.field} className="caption-xs text-amber-800/80 dark:text-amber-200/80">
                    <span className="font-medium">{f.field === "from" ? "fromFilter" : "subjectFilter"}</span> &ldquo;
                    {f.filter}&rdquo; {f.ok ? "found" : "not found"} in {f.field} &ldquo;{f.value || "(empty)"}
                    &rdquo;
                  </li>)}
              </ul>
            </div>}
          <p className="caption-xs text-ds-text-muted">Preview only. Actual matching may differ slightly.</p>
        </div>}
      {autoFields.length > 0 && <div className="min-w-0">
          {tokenPricePreview ? <>
              <p className="text-ds-text-primary text-sm font-medium">
                Sample event:{" "}
                <span className="inline-flex items-center gap-1.5 align-middle">
                  <TokenIcon symbol={selectedAssetLabel} size={16} enableStockTickers />
                  <span>{selectedAssetLabel}</span>
                </span>{" "}
                at ${tokenPricePreview.samplePrice !== undefined ? tokenPricePreview.samplePrice.toLocaleString() : "0"}
              </p>
              <p className="text-ds-text-muted mt-0.5 text-xs">
                Chosen to satisfy your condition: {tokenPricePreview.operationLabel} $
                {tokenPriceTarget !== undefined ? tokenPriceTarget.toLocaleString() : "0"}.
              </p>
            </> : <>
              <p className="text-ds-text-primary text-sm">
                Test{" "}
                {autoFields.map(f => <span key={f.key}>
                    <span className="font-semibold">{f.key}</span>
                    {" = "}
                    <span className="font-semibold tabular-nums">{String(f.value)}</span>
                  </span>)}
              </p>
              <p className="text-ds-text-muted mt-0.5 text-xs">
                Value chosen to satisfy your trigger condition. Click Run test to run.
              </p>
            </>}
        </div>}

      <div className={`flex flex-col ${isEmailTrigger ? "gap-4" : "divide-ds-neutral-200/70 dark:divide-ds-neutral-800 gap-3 divide-y"}`}>
        {editableFields.map(field => {
        const value = formValues[field.key] ?? "";
        const isEmailRecipientsField = isEmailTrigger && field.key === "to" && field.type === "array";
        const isMultiline = field.type === "object" || field.type === "array";
        const isBoolean = field.type === "boolean";
        const header = <div className="mb-1 space-y-0.5">
              <span className="field-label">{field.key}</span>
              {field.description && <p className="body-sm text-ds-text-muted">{field.description}</p>}
            </div>;
        return <div key={field.key}>
              {isBoolean ? <label className="block">
                  {header}
                  <select value={value} onChange={e => handleFieldChange(field.key, e.target.value)} className="border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card w-full rounded-lg border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                </label> : isMultiline ? isEmailRecipientsField ? <EmailRecipientsInput label={field.key} description={field.description} value={value} onChange={val => handleFieldChange(field.key, val)} /> : <JsonEditorBase label={field.key} description={field.description} value={value} onChange={val => handleFieldChange(field.key, val)} placeholder={field.type === "array" ? "[]" : "{}"} minHeight="120px" /> : <label className="block">
                  {header}
                  <input type="text" inputMode={field.type === "number" || field.type === "integer" ? "decimal" : "text"} value={value} onChange={e => handleFieldChange(field.key, e.target.value)} className="border-ds-neutral-300 text-ds-text-primary placeholder:text-ds-text-disabled dark:border-ds-neutral-700 bg-ds-surface-card w-full rounded-lg border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                </label>}
            </div>;
      })}
      </div>
    </div>;
}